import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import ListSubheader from '@mui/material/ListSubheader';
import MuiTextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';

import { labelSx } from './PartsShipmentsHelpers';
import { LocationNodeData } from '../../../../../@types/live-search.type';
import { useSearchNodeDispatch } from '../../../../../queries/live-search-query';
import { displayNodeTypeName } from '../../../../../utils/node-type';

/**
 * A node option offered before the user types — e.g. the dispatch destination
 * suggestions for a job (assigned technician, their parent depot, the job
 * location). `section` carries the BFF grouping ('Structure' / 'Service') and
 * drives the grouped dropdown rendering.
 */
export type NodeSearchOption = LocationNodeData & { section?: string };

export interface NodeSearchFieldProps {
  label: string;
  defaultValue?: LocationNodeData | null;
  defaultInputValue?: string;
  /**
   * Curated options shown when the field is opened before the user types
   * (and kept above search results once they do). Without this, the dropdown
   * stays empty until the user types two characters.
   */
  suggestions?: NodeSearchOption[];
  onChange: (node: LocationNodeData | null) => void;
}

const optionMetaSx = { fontSize: '10px', color: 'text.secondary' };

// Search hits that duplicate a suggested node (matched by id) are dropped so
// the dropdown never lists the same destination twice.
const withoutSuggested = (results: LocationNodeData[], suggested: NodeSearchOption[]) =>
  results.filter((node) => suggested.every((s) => s.id !== node.id));

export const NodeSearchField: React.FC<NodeSearchFieldProps> = ({
  label,
  defaultValue,
  defaultInputValue,
  suggestions,
  onChange,
}) => {
  const [inputValue, setInputValue] = useState(defaultValue?.name ?? defaultInputValue ?? '');
  const [options, setOptions] = useState<NodeSearchOption[]>([]);
  const [selected, setSelected] = useState<NodeSearchOption | null>(defaultValue ?? null);
  const [seeded, setSeeded] = useState<boolean>(
    Boolean(defaultValue) || Boolean(defaultInputValue)
  );
  // Only text the user actually typed. The seeded default (e.g. the job
  // location name) must NOT be used as a search term — searching with it
  // returns nothing useful and hides the suggestions list.
  const [searchTerm, setSearchTerm] = useState('');
  const searchNodes = useSearchNodeDispatch();

  useEffect(() => {
    if (!seeded && defaultValue && selected === null && inputValue === '') {
      setSelected(defaultValue);
      setInputValue(defaultValue.name);
      setSeeded(true);
    }
  }, [defaultValue?.id, defaultValue?.name, seeded, selected, inputValue]);

  useEffect(() => {
    const suggested = suggestions ?? [];
    if (searchTerm.length < 2) {
      setOptions(suggested);
      return;
    }
    const timer = setTimeout(() => {
      searchNodes
        .mutateAsync({ name: searchTerm })
        .then((results) => setOptions([...suggested, ...withoutSuggested(results, suggested)]))
        .catch(() => setOptions(suggested));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, suggestions]);

  const hasSuggestions = (suggestions ?? []).length > 0;

  return (
    <Box>
      <Typography sx={labelSx}>{label}</Typography>
      <Autocomplete
        size="small"
        options={options}
        getOptionLabel={(opt) => opt.name}
        filterOptions={(x) => x}
        value={selected}
        inputValue={inputValue}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        onInputChange={(_, val, reason) => {
          setInputValue(val);
          if (reason === 'input') {
            setSeeded(true);
            setSearchTerm(val);
            if (selected && val !== selected.name) {
              setSelected(null);
              onChange(null);
            }
          }
        }}
        onChange={(_, val) => {
          setSelected(val);
          // Reset so reopening the field offers the suggestions again instead
          // of stale results for whatever was last typed.
          setSearchTerm('');
          onChange(val);
        }}
        loading={searchNodes.isPending}
        noOptionsText={searchTerm.length < 2 ? 'Start typing to search…' : 'No matches'}
        groupBy={hasSuggestions ? (opt) => opt.section ?? 'Search results' : undefined}
        renderGroup={
          hasSuggestions
            ? (params) => (
                <li key={params.key}>
                  <ListSubheader
                    component="div"
                    sx={{
                      fontSize: '11px',
                      fontWeight: 600,
                      lineHeight: '28px',
                      bgcolor: 'v4.background',
                      color: 'text.secondary',
                    }}
                  >
                    {params.group}
                  </ListSubheader>
                  <Box component="ul" sx={{ p: 0, m: 0 }}>
                    {params.children}
                  </Box>
                </li>
              )
            : undefined
        }
        renderInput={(params) => (
          <MuiTextField
            {...params}
            placeholder={`Search ${label.toLowerCase()}...`}
            inputProps={{ ...params.inputProps, 'aria-label': label }}
            sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={`${option.id}-${option.code ?? option.section ?? ''}`}>
            <Box>
              <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{option.name}</Typography>
              <Typography sx={optionMetaSx}>
                {displayNodeTypeName(option.nodeTypeName)}
                {option.code ? ` — ${option.code}` : ''}
                {option.locationHierarchy ? ` · ${option.locationHierarchy}` : ''}
                {option.clientContractName ? ` · ${option.clientContractName}` : ''}
              </Typography>
              {option.serviceAddress && (
                <Typography sx={optionMetaSx}>{option.serviceAddress}</Typography>
              )}
              {option.logisticsAddress && (
                <Typography sx={optionMetaSx}>{option.logisticsAddress}</Typography>
              )}
              {option.mainAddress && (
                <Typography sx={optionMetaSx}>{option.mainAddress}</Typography>
              )}
            </Box>
          </li>
        )}
      />
    </Box>
  );
};
